/**
 * Generates Zod validation schema from FormDefinition fields
 */

import { z, ZodTypeAny } from 'zod';
import { FormDefinition, FormField } from '../../api/types';

/**
 * Generate a Zod schema for a single field
 */
function generateFieldSchema(field: FormField): ZodTypeAny {
  const { type, required, validation } = field;

  switch (type) {
    case 'text':
    case 'textarea': {
      let schema = z.string();

      if (required) {
        schema = schema.min(1, validation?.message || 'Dieses Feld ist erforderlich');
      }

      if (validation?.min) {
        schema = schema.min(validation.min, validation.message || `Mindestens ${validation.min} Zeichen`);
      }
      if (validation?.max) {
        schema = schema.max(validation.max, validation.message || `Maximal ${validation.max} Zeichen`);
      }
      if (validation?.pattern) {
        schema = schema.regex(new RegExp(validation.pattern), validation.message || 'Ungültiges Format');
      }

      return required ? schema : schema.optional();
    }

    case 'email': {
      let schema = z.string().email(validation?.message || 'Bitte geben Sie eine gültige E-Mail-Adresse ein');

      if (validation?.min) {
        schema = schema.min(validation.min, validation.message);
      }
      if (validation?.max) {
        schema = schema.max(validation.max, validation.message);
      }

      return required ? schema : schema.optional();
    }

    case 'number': {
      let schema = z.coerce.number();

      if (validation?.min !== undefined) {
        schema = schema.min(validation.min, validation.message || `Mindestwert: ${validation.min}`);
      }
      if (validation?.max !== undefined) {
        schema = schema.max(validation.max, validation.message || `Maximalwert: ${validation.max}`);
      }

      return required ? schema : schema.optional();
    }

    case 'date': {
      let schema = z.string();

      if (required) {
        schema = schema.min(1, validation?.message || 'Bitte wählen Sie ein Datum');
      }

      return required ? schema : schema.optional();
    }

    case 'select': {
      let schema = z.string();

      if (required) {
        schema = schema.min(1, validation?.message || 'Bitte wählen Sie eine Option');
      }

      return required ? schema : schema.optional();
    }

    case 'checkbox': {
      if (required) {
        return z.boolean().refine((val) => val === true, {
          message: validation?.message || 'Sie müssen diesem Feld zustimmen',
        });
      }
      return z.boolean();
    }

    case 'file': {
      if (required) {
        return z.any().refine((val) => val instanceof File, {
          message: validation?.message || 'Bitte wählen Sie eine Datei aus',
        });
      }
      return z.any().optional();
    }

    default:
      return z.string().optional();
  }
}

/**
 * Generate a complete Zod schema from FormDefinition
 */
export function generateZodSchema(formDefinition: FormDefinition): z.ZodObject<Record<string, ZodTypeAny>> {
  const shape: Record<string, ZodTypeAny> = {};

  for (const field of formDefinition.fields) {
    shape[field.name] = generateFieldSchema(field);
  }

  return z.object(shape);
}

/**
 * Generate default values for the form based on field types
 */
export function generateDefaultValues(formDefinition: FormDefinition): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};

  for (const field of formDefinition.fields) {
    switch (field.type) {
      case 'checkbox':
        defaults[field.name] = false;
        break;
      case 'number':
        defaults[field.name] = '';
        break;
      case 'file':
        defaults[field.name] = null;
        break;
      default:
        defaults[field.name] = '';
    }
  }

  return defaults;
}
